/* import __COLOCATED_TEMPLATE__ from './email-metadata.hbs'; */
/* RESPONSIBLE TEAM: team-channels */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import {
  type EmailHeaderAddress,
  type EmailMetadata,
} from 'embercom/objects/inbox/renderable/email-metadata';

interface Args {
  metadata?: EmailMetadata;
}

interface Signature {
  Args: Args;
}

const LEARN_MORE_URL = 'https://www.intercom.com/help/en/articles/email-delivery';

export default class EmailMetadataComponent extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked isCollapsed = true;

  headersNames = ['reply_to', 'to', 'cc', 'bcc'];

  get headerAddresses() {
    return this.args.metadata?.headerAddresses;
  }

  get headers() {
    let headers: { [key: string]: string } = {};
    if (!this.headerAddresses) {
      return headers;
    }
    for (let headerName of this.headersNames) {
      // @ts-ignore
      let headerValue = this.formatAddressList(this.headerAddresses[headerName]);
      if (headerValue) {
        headers[headerName] = headerValue;
      }
    }
    return headers;
  }

  get date() {
    return this.args.metadata?.date || '';
  }

  get subject() {
    return this.args.metadata?.subject || '';
  }

  get toAndCcRecipients() {
    return this.getFormattedRecipients(['to', 'cc']);
  }

  get bccRecipients() {
    return this.getFormattedRecipients(['bcc']);
  }

  get toAndCcAddresses(): EmailHeaderAddress[] {
    return this.getAddresses(['to', 'cc']);
  }

  get bccAddresses(): EmailHeaderAddress[] {
    return this.getAddresses(['bcc']);
  }

  get headerAddressArrays(): { [key: string]: EmailHeaderAddress[] } {
    let result: { [key: string]: EmailHeaderAddress[] } = {};
    if (!this.headerAddresses) {
      return result;
    }
    for (let headerName of this.headersNames) {
      // @ts-ignore - dynamic property access on HeaderAddresses
      let addresses = this.headerAddresses[headerName] as EmailHeaderAddress[] | undefined;
      if (Array.isArray(addresses) && addresses.length > 0) {
        result[headerName] = addresses;
      }
    }
    return result;
  }

  get learnMoreUrl(): string {
    return LEARN_MORE_URL;
  }

  get dropReasonTexts(): Record<string, string> {
    return {
      user_suppressed: this.intl.t(
        'channels.conversation-stream.email-metadata.drop-reasons.user-suppressed',
      ),
      email_address_ignored: this.intl.t(
        'channels.conversation-stream.email-metadata.drop-reasons.email-address-ignored',
      ),
      user_bounced: this.intl.t(
        'channels.conversation-stream.email-metadata.drop-reasons.user-bounced',
      ),
      user_not_contactable: this.intl.t(
        'channels.conversation-stream.email-metadata.drop-reasons.user-not-contactable',
      ),
      banned_domain: this.intl.t(
        'channels.conversation-stream.email-metadata.drop-reasons.banned-domain',
      ),
    };
  }

  get defaultDropReasonText(): string {
    return this.intl.t('channels.conversation-stream.email-metadata.drop-reasons.default');
  }

  toLowerCase(text: string): string {
    return text?.toLowerCase() || '';
  }

  nameOrAddress(address: EmailHeaderAddress) {
    if (!address.name) {
      return address.email;
    }
    return address.name;
  }

  get fromAddress() {
    let from = this.headerAddresses?.from;
    if (!Array.isArray(from) || from.length === 0) {
      return { name: '', email: '' };
    }

    return {
      name: (from[0].name ?? '').trim(),
      email: (from[0].email ?? '').trim(),
    };
  }

  get replyToAddress() {
    let replyTo = this.headerAddresses?.reply_to;
    if (!Array.isArray(replyTo) || replyTo.length === 0) {
      return null;
    }

    return {
      name: (replyTo[0].name ?? '').trim(),
      email: (replyTo[0].email ?? '').trim(),
    };
  }

  get isReplyToDifferent() {
    if (!this.replyToAddress) {
      return false;
    }

    return this.replyToAddress.email.toLowerCase() !== this.fromAddress.email.toLowerCase();
  }

  formatAddress(address: EmailHeaderAddress) {
    if (!address.name) {
      return address.email;
    }
    return `${address.name} <${address.email}>`;
  }

  formatAddressList(addresses: EmailHeaderAddress[]) {
    if (!addresses) {
      return '';
    }
    return addresses.map((address) => this.formatAddress(address)).join(', ');
  }

  get isTextSelected() {
    let selection = window.getSelection();
    if (selection) {
      return selection.toString() !== '';
    }
    return false;
  }

  private getAddresses(types: ('to' | 'cc' | 'bcc')[]): EmailHeaderAddress[] {
    if (!this.headerAddresses) {
      return [];
    }
    return types.reduce((acc, type) => {
      let addresses = this.headerAddresses?.[type];
      if (Array.isArray(addresses)) {
        acc.push(...addresses);
      }
      return acc;
    }, [] as EmailHeaderAddress[]);
  }

  private getFormattedRecipients(types: ('to' | 'cc' | 'bcc')[]) {
    if (!this.headerAddresses) {
      return '';
    }

    let addresses = types.reduce((acc, type) => {
      let addresses = this.headerAddresses?.[type];
      if (Array.isArray(addresses)) {
        acc.push(...addresses);
      }
      return acc;
    }, [] as EmailHeaderAddress[]);

    return addresses.map((address) => this.nameOrAddress(address)).join(', ');
  }

  @action toggleCollapseState(): void {
    if (this.isTextSelected) {
      return;
    }
    this.isCollapsed = !this.isCollapsed;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::EmailMetadata': typeof EmailMetadataComponent;
    'inbox2/conversation-stream/part-groups/email-metadata': typeof EmailMetadataComponent;
  }
}
